import React, { FC, useEffect, useState } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { useNavigate } from 'react-router-dom';
import { Unity, useUnityContext } from 'react-unity-webgl';

import Lottie from 'lottie-react';
import { ReactUnityEventParameter } from 'react-unity-webgl/distribution/types/react-unity-event-parameters';

import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {
  Box,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';

import catAnim from '@assets/lottie/catAnim.json';

import { useResponsiveUnity } from './hooks';
import { UnityComponentProps } from './types';

import './index.scss';

// Добавляем Google Font
// const fontStyle = {
//   fontFamily: "'Comfortaa', sans-serif",
//   fontWeight: 700,
//   fontSize: '32px',
// };

const getLoadingText = (percentage: number): string => {
  if (percentage < 25) return 'Собираем игру для вас с любовью';
  if (percentage < 50) return 'Загружаем игровые ресурсы';
  if (percentage < 90) return 'Почти готово!';
  return 'Мы не зависли, просто финальные штрихи';
};

export const UnityComponent: FC<UnityComponentProps> = ({
  loaderUrl,
  dataUrl,
  frameworkUrl,
  isFullScreenEnabled = false,
  codeUrl,
  resolution,
}) => {
  const [devicePixelRatio, setDevicePixelRatio] = useState(
    window.devicePixelRatio
  );
  const [unityError, setUnityError] = useState<string | null>(null);

  const unityContext = useUnityContext({
    loaderUrl,
    dataUrl,
    frameworkUrl,
    codeUrl,
  });

  useResponsiveUnity(resolution);

  const {
    unityProvider,
    loadingProgression,
    isLoaded,
    addEventListener,
    removeEventListener,
  } = unityContext;

  const handle = useFullScreenHandle();

  const navigate = useNavigate();

  const reloadPage = () => {
    navigate('/game');
  };

  useEffect(() => {
    addEventListener('ExitGame', reloadPage as () => ReactUnityEventParameter);

    return () => {
      removeEventListener(
        'ExitGame',
        reloadPage as () => ReactUnityEventParameter
      );
    };
  }, [addEventListener, removeEventListener]);

  useEffect(() => {
    const updateDevicePixelRatio = function () {
      setDevicePixelRatio(window.devicePixelRatio);
    };
    const mediaMatcher = window.matchMedia(
      `screen and (resolution: ${devicePixelRatio}dppx)`
    );

    mediaMatcher.addEventListener('change', updateDevicePixelRatio);

    return () => {
      mediaMatcher.removeEventListener('change', updateDevicePixelRatio);
    };
  }, [devicePixelRatio]);

  // Глобальный обработчик ошибок для WebGPU
  useEffect(() => {
    const handleGlobalError = (event: ErrorEvent) => {
      if (event.error && event.error.message && 
          (event.error.message.includes('WebGPU') || 
           event.error.message.includes('wgpu') ||
           event.error.message.includes('graphics API'))) {
        console.warn('WebGPU error detected, attempting fallback:', event.error);
        setUnityError('Ваше устройство не поддерживает современные графические технологии. Попробуйте обновить браузер или использовать другое устройство.');
      }
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason && event.reason.message && 
          (event.reason.message.includes('WebGPU') || 
           event.reason.message.includes('wgpu'))) {
        console.warn('WebGPU promise rejection:', event.reason);
        setUnityError('Ваше устройство не поддерживает современные графические технологии. Попробуйте обновить браузер или использовать другое устройство.');
      }
    });

    return () => {
      window.removeEventListener('error', handleGlobalError);
    };
  }, []);

  const loadingPercentage = Math.round(loadingProgression * 100);

  const { active, exit, enter } = handle;

  const fullScreenTitle = active
    ? 'Нажмите, чтобы выйти из полноэкранного режима'
    : 'Нажмите, чтобы войти в полноэкранный режим';

  return (
    <FullScreen className="fullScreenContainer" handle={handle}>
      <Box id="unity-container" className="unityContainer">
        {unityError ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'white',
            textAlign: 'center',
            padding: '20px'
          }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Ошибка загрузки игры
            </Typography>
            <Typography sx={{ marginBottom: 3, maxWidth: '400px' }}>
              {unityError}
            </Typography>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f3c1c8',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Попробовать снова
            </button>
          </Box>
        ) : (
          <>
            <Unity
              unityProvider={unityProvider}
              className="unityGame"
              devicePixelRatio={devicePixelRatio}
            />
            {!isLoaded && (
              <>
                <Box className="loadingContainer">
                  <LinearProgress
                    variant="determinate"
                    value={loadingPercentage}
                    className="linearProgress"
                  />
                  <Typography className="percentageContainer" sx={{
                    fontSize: 'clamp(8px, 2vw, 14px)'
                  }}>{`${loadingPercentage}%`}</Typography>
                </Box>
                {/* <Typography sx={{ 
                  color: 'rgb(225, 150, 160)',
                  textAlign: 'center', 
                  position: 'absolute',
                  bottom: '20%',
                  left: 0,
                  right: 0,
                  margin: '0 auto',
                  fontFamily: "'Comfortaa', sans-serif",
                  fontWeight: 700,
                  fontSize: '32px',
                }} className="pulseText">
                  {getLoadingText(loadingPercentage)}
                </Typography> */}
                {/* Lottie анимация кота снизу по центру */}
                <Box sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: '2%',
                  display: 'flex',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  zIndex: 10,
                }}>
                  <Lottie
                    animationData={catAnim}
                    loop={true}
                    style={{ width: 220, height: 90 }}
                  />
                </Box>
              </>
            )}
            {isFullScreenEnabled && (
              <Tooltip title={fullScreenTitle} placement="left">
                <IconButton
                  className="fullScreenIconContainer"
                  onClick={active ? void exit : void enter}
                >
                  {active ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Box>
    </FullScreen>
  );
};
